import { Check, ChefHat, LoaderCircle, ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { BurgerStack, type StackLayer } from "../components/builder/BurgerStack";
import { PageHeader } from "../components/sections/PageHeader";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { QuantitySelector } from "../components/ui/QuantitySelector";
import { Reveal } from "../components/ui/Reveal";
import { SectionHeading } from "../components/ui/SectionHeading";
import { useCart } from "../context/CartContext";
import { useCustomizer } from "../context/CustomizerContext";
import type { BuilderGroup, CustomizationCatalog, Extra, Product } from "../types";
import { builderRuleText } from "../utils/builder";
import { cn, formatPrice } from "../utils/format";

type BuilderConfig = NonNullable<CustomizationCatalog["builder"]>;

const MAX_ITEMS = 20;
const MAX_NOTES = 200;
const round2 = (value: number) => Math.round(value * 100) / 100;

const Builder = ({ builder }: { builder: BuilderConfig }) => {
  const { addItem, openCart } = useCart();
  const [picks, setPicks] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);

  const qty = (id: number) => picks[id] ?? 0;
  const groupTotal = (g: BuilderGroup) => g.ingredients.reduce((acc, i) => acc + qty(i.id), 0);
  const missing = builder.groups.filter((g) => groupTotal(g) < g.min);

  const chosen = builder.groups.flatMap((g) =>
    g.ingredients.filter((i) => qty(i.id) > 0).map((i) => ({ ...i, quantity: qty(i.id), slug: g.slug })),
  );
  const layers: StackLayer[] = chosen.map(({ id, name, slug, quantity: q }) => ({ id, name, slug, quantity: q }));
  const unitPrice = round2(builder.product.price + chosen.reduce((acc, c) => acc + c.price * c.quantity, 0));
  const total = round2(unitPrice * quantity);

  const isSingle = (g: BuilderGroup) => g.max === 1 && g.maxPerItem === 1;

  const toggle = (g: BuilderGroup, ingredient: Extra) =>
    setPicks((prev) => {
      const next = { ...prev };
      const selected = (prev[ingredient.id] ?? 0) > 0;
      if (isSingle(g)) {
        // Escolha única: troca a opção (e só desmarca se a categoria for opcional).
        g.ingredients.forEach((i) => delete next[i.id]);
        if (!selected || g.min > 0) next[ingredient.id] = 1;
        return next;
      }
      const total = g.ingredients.reduce((acc, i) => acc + (prev[i.id] ?? 0), 0);
      if (selected) delete next[ingredient.id];
      else if (total < g.max) next[ingredient.id] = 1;
      return next;
    });

  const step = (g: BuilderGroup, ingredient: Extra, delta: number) =>
    setPicks((prev) => {
      const current = prev[ingredient.id] ?? 0;
      const total = g.ingredients.reduce((acc, i) => acc + (prev[i.id] ?? 0), 0);
      const value = current + delta;
      if (value < 0 || value > g.maxPerItem || (delta > 0 && total >= g.max)) return prev;
      return { ...prev, [ingredient.id]: value };
    });

  const handleAdd = () => {
    if (missing.length > 0) return;
    const product: Product = {
      id: builder.product.id,
      name: builder.product.name,
      description: builder.product.description,
      price: builder.product.price,
      category: builder.product.category,
      image_url: builder.product.image_url,
      is_builder: true,
    };
    addItem(
      product,
      { removed: [], extras: chosen.map(({ id, name, price, quantity: q }) => ({ id, name, price, quantity: q })), notes },
      quantity,
    );
    toast.success("Seu lanche foi para o carrinho!");
    setPicks({});
    setNotes("");
    setQuantity(1);
    openCart();
  };

  const addButton = (className?: string) => (
    <Button
      size="lg"
      onClick={handleAdd}
      disabled={missing.length > 0}
      icon={<ShoppingCart className="size-5" aria-hidden="true" />}
      iconPosition="left"
      className={cn("min-w-0 px-5", className)}
    >
      <span className="hidden sm:inline">Adicionar ao carrinho</span>
      <span className="sm:hidden">Adicionar</span>
      <span aria-hidden="true">·</span>
      <span className="tabular-nums">{formatPrice(total)}</span>
    </Button>
  );

  const missingText = missing.length > 0 ? `Falta escolher: ${missing.map((g) => g.name.toLowerCase()).join(", ")}` : null;

  return (
    <div className="mt-12 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start lg:gap-10">
      <div className="space-y-10">
        {builder.groups.map((g, index) => {
          const total = groupTotal(g);
          const done = total >= g.min && g.min > 0;
          return (
            <section key={g.slug} aria-labelledby={`grupo-${g.slug}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 id={`grupo-${g.slug}`} className="flex items-center gap-3 font-display text-3xl tracking-wide">
                  <span className="grid size-9 place-items-center rounded-full bg-primary-soft font-sans text-sm font-bold text-primary">
                    {index + 1}
                  </span>
                  {g.name}
                </h3>
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
                    done ? "bg-success/15 text-success" : g.min > 0 ? "bg-danger/10 text-danger" : "bg-card text-muted",
                  )}
                >
                  {done && <Check className="size-3.5" aria-hidden="true" />}
                  {builderRuleText(g)}
                  {g.max > 1 && ` · ${total}/${g.max}`}
                </span>
              </div>

              <ul className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {g.ingredients.map((ingredient) => {
                  const current = qty(ingredient.id);
                  const selected = current > 0;
                  const full = total >= g.max && !selected && !isSingle(g);
                  const price = <span className="block text-xs text-primary">+ {formatPrice(ingredient.price)}</span>;
                  return (
                    <li key={ingredient.id}>
                      {g.maxPerItem === 1 ? (
                        <button
                          type="button"
                          onClick={() => toggle(g, ingredient)}
                          aria-pressed={selected}
                          disabled={full}
                          className={cn(
                            "flex min-h-16 w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                            selected ? "border-primary bg-primary-soft" : "border-line bg-card hover:border-primary/50",
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={cn(
                              "grid size-5 shrink-0 place-items-center border transition-colors",
                              isSingle(g) ? "rounded-full" : "rounded-md",
                              selected ? "border-primary bg-primary text-night" : "border-muted/50",
                            )}
                          >
                            {selected && <Check className="size-3.5" strokeWidth={3} />}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">{ingredient.name}</span>
                            {price}
                          </span>
                        </button>
                      ) : (
                        <div
                          className={cn(
                            "flex min-h-16 items-center justify-between gap-3 rounded-2xl border p-4 transition-colors",
                            selected ? "border-primary bg-primary-soft" : "border-line bg-card",
                          )}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">{ingredient.name}</span>
                            {price}
                          </span>
                          <QuantitySelector
                            size="sm"
                            min={0}
                            max={Math.min(g.maxPerItem, current + Math.max(0, g.max - total))}
                            quantity={current}
                            label={ingredient.name}
                            onIncrement={() => step(g, ingredient, 1)}
                            onDecrement={() => step(g, ingredient, -1)}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        <div>
          <label htmlFor="builder-notes" className="flex items-baseline justify-between gap-3">
            <span className="font-display text-3xl tracking-wide">Observação</span>
            <span className="text-xs text-muted">
              {notes.length}/{MAX_NOTES}
            </span>
          </label>
          <textarea
            id="builder-notes"
            maxLength={MAX_NOTES}
            className="field mt-4 h-24 resize-none py-3"
            placeholder="Ex.: carne ao ponto, pão bem tostado"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <aside className="mt-12 lg:sticky lg:top-28 lg:mt-0" aria-label="Resumo do seu lanche">
        <Card className="overflow-hidden">
          <div className="bg-dots border-b border-line bg-night px-6 pb-6 pt-8">
            <BurgerStack layers={layers} />
          </div>
          <div className="p-6">
            <h2 className="font-display text-3xl tracking-wide">Seu lanche</h2>
            {chosen.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Comece escolhendo o pão e a carne.</p>
            ) : (
              <ul className="mt-3 space-y-1.5 text-sm">
                <AnimatePresence initial={false}>
                  {chosen.map((c) => (
                    <motion.li
                      key={c.id}
                      layout
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="flex justify-between gap-3"
                    >
                      <span className="text-white/90">
                        {c.quantity > 1 && `${c.quantity}x `}
                        {c.name}
                      </span>
                      <span className="text-muted">{formatPrice(c.price * c.quantity)}</span>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            )}

            <div className="mt-5 flex items-center justify-between border-t border-line pt-5">
              <QuantitySelector
                min={1}
                max={MAX_ITEMS}
                quantity={quantity}
                label="Monte seu Lanche"
                onIncrement={() => setQuantity((q) => Math.min(MAX_ITEMS, q + 1))}
                onDecrement={() => setQuantity((q) => Math.max(1, q - 1))}
              />
              <div className="text-right" aria-live="polite">
                <p className="text-xs text-muted">
                  {quantity > 1 ? `${quantity} × ${formatPrice(unitPrice)}` : "Total"}
                </p>
                <p className="text-2xl font-bold text-primary">{formatPrice(total)}</p>
              </div>
            </div>

            <div className="mt-5 hidden lg:block">
              {addButton("w-full")}
              {missingText && <p className="mt-3 text-center text-xs text-danger">{missingText}</p>}
            </div>
          </div>
        </Card>
      </aside>

      {/* Barra fixa no celular/tablet */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-coal/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur lg:hidden">
        {missingText && <p className="mb-2 text-center text-xs text-danger">{missingText}</p>}
        {addButton("w-full")}
      </div>
    </div>
  );
};

const MonteSeuLanche = () => {
  const { status, builder } = useCustomizer();

  return (
    <>
      <PageHeader title="Monte seu Lanche" current="Monte seu Lanche" />
      <section className="mx-auto max-w-[1400px] px-4 pb-36 pt-12 sm:px-6 lg:px-8 lg:pb-24 lg:pt-16">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="Do seu jeito"
            title="Monte seu"
            highlight="Lanche"
            description="Escolha cada camada: pão, carne, queijo, salada, molho e adicionais. O preço muda conforme você monta."
          />
        </Reveal>

        {status === "loading" ? (
          <div className="mt-16 flex justify-center" aria-label="Carregando">
            <LoaderCircle className="size-8 animate-spin text-primary" aria-hidden="true" />
          </div>
        ) : builder ? (
          <Builder builder={builder} />
        ) : (
          <div className="mx-auto mt-14 flex max-w-md flex-col items-center rounded-card border border-dashed border-line px-6 py-14 text-center">
            <ChefHat className="size-10 text-primary" aria-hidden="true" />
            <p className="mt-4 font-display text-3xl tracking-wide">Indisponível no momento</p>
            <p className="mt-1 text-sm text-muted">O Monte seu Lanche volta logo. Enquanto isso, que tal um dos nossos burgers?</p>
            <Button to="/cardapio" className="mt-6">
              Ver cardápio
            </Button>
          </div>
        )}
      </section>
    </>
  );
};

export default MonteSeuLanche;
